import { compact } from "lodash";

import { ModelEventConstructor } from "../../utils/ModelEventConstructor";
import SubscriptionPool from "../../redux/services/SubscriptionPool";
import createLogger from "../../utils/logger.util";
import { SortingOrder } from "../../types/SortingOrder";
import Subscription from "../../types/Subscription";
import ServiceSubscribeOptionsBase from "../../types/ServiceSubscribeOptionsBase";
import { PaymentAccount } from "../../types/PaymentAccount";
import {
	TaxiServiceOld as TaxiService,
	Base,
	Language,
	Dispatcher,
	Executor,
	Car,
	Card,
} from "..";

const logger = createLogger({ name: "CarPark" });

class CarPark extends Base {
	// Its needed due to typescript bundler conflict
	private static _Card: Card | null = null;

	public static get Card() {
		if (this._Card) return this._Card;

		this._Card = new Card((prpc) => prpc.theirsModel.fleet.card);

		return this._Card;
	}

	public static fromResponse(data: any): CarPark.Model {
		const retval: CarPark.Model = {
			id: data.id,

			taxiServices: data?.fleetToTaxiServices?.map((fleetToTaxiService) =>
				TaxiService.fromResponse(fleetToTaxiService.taxiService),
			),
			dispatchers: data?.fleetToDispatchers?.map((fleetToDispatcher) =>
				Dispatcher.fromResponse(fleetToDispatcher?.dispatcher),
			),
			executors: compact(data?.executors?.map(Executor.fromResponse)),
			cars: data?.cars?.map(Car.fromResponse),

			name: data.name,
			description: data.description,

			active: data.active,
			paymentAccounts: data.paymentAccounts,
			idno: data.idno ?? "",

			createdAt: data.createdAt,
			updatedAt: data.updatedAt,
			deletedAt: data.deletedAt,
		};

		return retval;
	}

	public static toRequest(
		model: CarPark.Model.New | CarPark.Model.Modified,
	): any {
		return {
			taxiServiceIds: model.taxiServiceIds,

			companyId: model.companyIds?.[0] ? model.companyIds[0] : undefined,
			dispatcherIds: model.dispatcherIds,
			executorIds: model.executorIds,
			carIds: model.carIds,
			name: model.name,
			description: model.description,
			idno: model.idno ?? "",

			active: model.active,
		};
	}

	public static async store(object: CarPark.Model.New) {
		try {
			const res = await this.request(
				(prpc) =>
					prpc.theirsModel.fleet.create(CarPark.toRequest(object)),
				{ silent: false, error: true },
			);
			logger.info("[CarPark] store", { object, res });
			return res;
		} catch (err: any) {
			logger.error("[CarPark] Error in store:", err);
			return null;
		}
	}

	public static async update(object: CarPark.Model.Modified) {
		try {
			const res = await this.request(
				(prpc) =>
					prpc.theirsModel.fleet.update(
						object.id,
						CarPark.toRequest(object),
					),
				{ silent: false, error: true },
			);
			logger.info("[CarPark] update", { object, res });

			return res;
		} catch (err: any) {
			logger.error("[CarPark] Error in update:", err);
			return null;
		}
	}

	public static async destroy(id: number[] | number) {
		try {
			let res;

			if (Array.isArray(id))
				res = await Promise.all(id.map((id) => this.destroyOne(id)));
			else res = await this.destroyOne(id);

			logger.info("[CarPark] destroy", { id, res });
		} catch (err) {
			logger.error("[CarPark] Error destroy:", { id, err });
		}
	}

	public static async getById(
		id: number,
	): Promise<CarPark.Model | undefined | null> {
		try {
			const res = await this.request(
				(prpc) => prpc.theirsModel.fleet.getById(id),
				{ silent: false, error: true },
			);

			logger.info("[CarPark] getById", { id, res });

			if (!res?.id) return null;

			return this.fromResponse(res);
		} catch (error) {
			logger.error("[CarPark] error getById", error);
			return null;
		}
	}

	public static async subscribe(
		options: CarPark.SubscribeOptions,
		onUpdate: Subscription.OnUpdate<CarPark.Model>,
	): Promise<Subscription<CarPark.SubscribeOptions> | null> {
		let _options = options; // ? new format of options in subscribe
		const modelEventConstructor = new ModelEventConstructor({
			onUpdate: (state) => {
				const models = compact(
					state.models.map(this.fromResponse) || [],
				);
				logger.info("[CarPark] subscribe", {
					_options,
					models: state.models,
					state,
				});
				onUpdate({
					...state,

					models,
				});
			},
		});
		const subscription = await SubscriptionPool.add(
			(prpc) =>
				prpc.theirsModel.fleet.subscribe({
					params: this.optionsToRequest(options),
					ping: () => true,
					onEvent: (events) => {
						modelEventConstructor.onEvent(events);
					},
					onError: (error) => {
						logger.error(error);
					},
				}),
			{ name: "CarPark.subscribe" },
		);

		return {
			unsubscribe: () => subscription.unsubscribe(),

			update: (options: CarPark.SubscribeOptions) => {
				const newOptions = this.optionsToRequest(options);

				_options = options;

				return subscription.update(newOptions);
			},
		} as Subscription<CarPark.SubscribeOptions>;
	}

	private static async destroyOne(id: number) {
		this.request((prpc) => prpc.theirsModel.fleet.delete(id));
	}

	private static optionsToRequest(options: CarPark.SubscribeOptions) {
		return {
			offset: options.offset,
			limit: options.limit,

			order: options.order,

			taxiServiceIds: options.taxiServiceIds,

			query: options.query,
			active: options.active,
			lang: options.language,
		};
	}
}

declare namespace CarPark {
	interface Model {
		id: number;

		taxiServices?: TaxiService.Model[];
		dispatchers?: Dispatcher.Model[];
		executors?: Executor.Model[];
		cars?: Car.Model[];

		name: string;
		description: string;

		active: boolean;
		paymentAccounts: PaymentAccount[];
		idno?: string | null;

		createdAt: string;
		updatedAt: string;
		deletedAt: string | null;
	}

	interface SubscribeOptions
		extends Omit<ServiceSubscribeOptionsBase<CarPark.Model>, "order"> {
		active?: boolean;
		language?: Language;
		taxiServiceIds?: number[];

		order?: Partial<Record<"name" | "active" | "createdAt", SortingOrder>>;
	}

	namespace Model {
		type NonEditablePropertyName =
			| "id"
			| "createdAt"
			| "updatedAt"
			| "deletedAt"
			| "paymentAccounts";

		type ModifiedPropertyName =
			| "taxiServices"
			| "dispatchers"
			| "executors"
			| "cars";

		interface ModifiedProperties {
			taxiServiceIds: number[];
			companyIds: number[];
			dispatcherIds: number[];
			executorIds: number[];
			carIds: number[];
		}

		type New = Omit<Model, NonEditablePropertyName | ModifiedPropertyName> &
			ModifiedProperties;

		type Modified = Partial<
			Omit<Model, NonEditablePropertyName | ModifiedPropertyName> &
				ModifiedProperties
		> &
			Pick<Model, "id">;
	}
}

export default CarPark;
